
import { defineComponent, reactive, onBeforeMount } from 'vue'

// @components
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import type { Courier } from '@/new-services/types/couriers'
import type { ManagerT } from '@/core/types/users.types'
import type { CompanyT } from '@/core/types/companies.types'
import type { InvoiceStatus } from '@/core/types/invoices.types'
import type { CityT } from '@/core/types/cities.types'
import type { GetCashPaymentStatusParams } from '@/new-services/types/cash'
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'

// realisations
import { users } from '@/core/realisations/users.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { invoices } from '@/core/realisations/invoices.realisation'
import { cities } from '@/core/realisations/cities.realisation'

// @new-services
import couriersAPI from '@/new-services/couriers.service'
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

const STATUSES = [
    { id: 1, name: 'Получено курьером' },
    { id: 2, name: 'Получено диспетчером' },
    { id: 3, name: 'Получено бухгалтером' },
    { id: 4, name: 'Ожидает оплаты' },
    { id: 5, name: 'Оплачено Kaspi' }
]

export default defineComponent({
    components: {
        NewInput,
        NewSelect,
        NewButton
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['submit'],
    setup(props, { emit }) {
        const state = reactive({
            courierId: null as number | null,
            date: null as string | null,
            invoiceNumber: null as string | null,
            statusIds: [] as Array<number>,
            managerIds: [] as Array<number>,
            companyIds: [] as Array<number>,
            invoiceStatusId: null as number | null,
            senderCity: null as number | null,
            receiverCity: null as number | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            senderDispatcherSectorId: null as number | null,
            receiverDispatcherSectorId: null as number | null
        })

        const data_state = reactive({
            couriers: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<Courier>
            },
            managers: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<ManagerT>
            },
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<CompanyT>
            },
            invoiceStatuses: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<InvoiceStatus>
            },
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<CityT>
            },
            dispatcherSectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<DispatcherSector>
            }
        })

        const loadManagers = async () => {
            try {
                data_state.managers.is_loading = true

                const response = await users.getManagers({
                    page: 1,
                    statusId: 1,
                    isExtended: 0
                })

                data_state.managers.list = response.data

                data_state.managers.is_failed = false
            } catch (error) {
                data_state.managers.is_failed = true
                console.error(error)
            } finally {
                data_state.managers.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getAllList({
                    page: 1,
                    limit: 9999,
                    isExtended: 0,
                    statusId: 2,
                })

                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
                console.error(error)
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadInvoiceStatuses = async () => {
            try {
                data_state.invoiceStatuses.is_loading = true

                const response = await invoices.getInvoiceStatuses()
                data_state.invoiceStatuses.list = response.data.filter((s) => s.id === 9 || s.id === 10)

                data_state.invoiceStatuses.is_failed = false
            } catch (error) {
                data_state.invoiceStatuses.is_failed = true
                console.error(error)
            } finally {
                data_state.invoiceStatuses.is_loading = false
            }
        }

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(1, undefined, 9999)
                data_state.cities.list = response.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const loadCouriers = async () => {
            try {
                data_state.couriers.is_loading = true

                const response = await couriersAPI.getCouriers({
                    page: 1,
                    limit: 9999,
                    statusIds: [2],
                })

                data_state.couriers.list = response.data.data

                data_state.couriers.is_failed = false
            } catch (error) {
                data_state.couriers.is_failed = true
                console.error(error)
            } finally {
                data_state.couriers.is_loading = false
            }
        }

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcherSectors.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                data_state.dispatcherSectors.list = response.data.data

                data_state.dispatcherSectors.is_failed = false
            } catch (error) {
                data_state.dispatcherSectors.is_failed = true
                console.error(error)
            } finally {
                data_state.dispatcherSectors.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCouriers()
            loadManagers()
            loadCompanies()
            loadInvoiceStatuses()
            loadCities()
            loadDispatcherSectors()
        })

        const onSubmit = () => {
            if (props.loading) return

            const params: Omit<GetCashPaymentStatusParams, 'page' | 'limit'> = {
                courierId: state.courierId,
                date: state.date,
                invoiceNumber: state.invoiceNumber,
                statusIds: state.statusIds,
                managerIds: state.managerIds,
                companyIds: state.companyIds,
                invoiceStatusId: state.invoiceStatusId,
                senderCity: state.senderCity,
                receiverCity: state.receiverCity,
                dateFrom: state.dateFrom,
                dateTo: state.dateTo,
                senderDispatcherSectorId: state.senderDispatcherSectorId,
                receiverDispatcherSectorId: state.receiverDispatcherSectorId,
            }

            emit('submit', params)
        }

        const onReset = () => {
            if (props.loading) return

            state.courierId = null
            state.date = null
            state.invoiceNumber = null
            state.statusIds = []
            state.managerIds = []
            state.companyIds = []
            state.invoiceStatusId = null
            state.senderCity = null
            state.receiverCity = null
            state.dateFrom = null
            state.dateTo = null
            state.senderDispatcherSectorId = null
            state.receiverDispatcherSectorId = null

            onSubmit()
        }

        return {
            STATUSES,
            state,
            data_state,
            loadCouriers,
            loadManagers,
            loadCompanies,
            loadInvoiceStatuses,
            loadCities,
            loadDispatcherSectors,
            onSubmit,
            onReset,
        }
    }
})
